import React from 'react';
import { ArrowDown, UserRound, Briefcase, Mic, GraduationCap, Users, BookOpen, Award, Compass, MessagesSquare, Building2 } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Cta } from '../Cta';
import { useScore } from '../ScoreContext';
const readerPath = ['Find what applies', 'Understand where they are', 'Take a useful next move', 'Indicate what they need'];
const knownContext = ['What matters', 'Where they are stuck', 'Actions they have taken', 'What kind of help may fit'];
const businessPaths = [{
  label: 'Coaching',
  Icon: UserRound
}, {
  label: 'Consulting',
  Icon: Briefcase
}, {
  label: 'Speaking',
  Icon: Mic
}, {
  label: 'Training',
  Icon: GraduationCap
}, {
  label: 'Workshops',
  Icon: Users
}, {
  label: 'Courses',
  Icon: BookOpen
}, {
  label: 'Certification',
  Icon: Award
}, {
  label: 'Advisory',
  Icon: Compass
}, {
  label: 'Community',
  Icon: MessagesSquare
}, {
  label: 'Team implementation',
  Icon: Building2
}];
const enablementGroups = [{
  label: 'Get in the room',
  items: ['Podcast and interview preparation', 'Keynote and speaking angles']
}, {
  label: 'Create the next experience',
  items: ['Workshop outlines', 'Training material', 'CTA paths']
}, {
  label: 'Continue the conversation',
  items: ['Follow-up assets', 'Offer language', 'Sales conversations', 'Content angles']
}];
function Down() {
  return <div className="flex justify-center py-1.5 md:py-2" data-id="element-907" data-mp="2m0i2hsznl78">
      <ArrowDown className="h-5 w-5 text-seaglass-light/70" aria-hidden="true" data-id="element-908" data-mp="2kfoauei06af" />
    </div>;
}
function Chips({
  items
}: {
  items: string[];
}) {
  return <div className="mt-2.5 flex flex-wrap gap-2 md:mt-0" data-id="element-909" data-mp="3oti60mmmiwyx">
      {items.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-910" data-mp="3ordvhlu7yzpo">
          {item}
        </span>)}
    </div>;
}
function Stage({
  n,
  title,
  children
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-white/12 bg-white/[0.05] px-4 py-4 md:grid md:grid-cols-[15rem_1fr] md:items-center md:gap-5 md:px-5 md:py-4" data-id="element-911" data-mp="3ktpwo7peomsi">
      <div className="flex items-center gap-3" data-id="element-912" data-mp="1ljnb13p7td6i">
        <span className="text-sm font-semibold text-seaglass-light/70" data-id="element-913" data-mp="1live9pjvt9en">{n}</span>
        <p className="font-serif text-xl text-paper md:text-2xl" data-id="element-914" data-mp="1glv8lfqy28o2">{title}</p>
      </div>
      <div className="mt-2.5 md:mt-0" data-id="element-915" data-mp="1cll30472dk9q">{children}</div>
    </div>;
}
export function RevenueArchitecture() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="bg-navy px-6 pb-14 pt-8 text-paper md:px-10 md:pb-16 md:pt-10" data-id="element-916" data-mp="24bfuo7ie9m1k">
      <div className="mx-auto max-w-5xl" data-id="element-917" data-mp="209kux7g43gpw">
        <div className="max-w-3xl" data-id="element-918" data-mp="2089oezva7uv3">
          <Reveal data-id="element-919" data-mp="20bff2jjk74sx">
            <h2 className="font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-920" data-mp="1varou035mrzr">
              A better reader path creates
              <br data-id="element-921" data-mp="ovgy7ti53eh5" />
              <span className="italic text-seaglass-light" data-id="element-922" data-mp="ow0gxcfm66w8">
                better business opportunities.
              </span>
            </h2>
            <p className="mt-3.5 text-lg leading-relaxed text-paper/70" data-id="element-923" data-mp="2viy570af9129">
              <span className="block" data-id="element-924" data-mp="2vglfg0yf9men">Readers get help using the book.</span>
              <span className="mt-1 block" data-id="element-925" data-mp="30iappedtlw1o">
                You gain the context, assets, and pathways to connect the right
                people to the right work.
              </span>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-926" data-mp="39bxgeq368940">
          <div className="mt-6 md:mt-7" data-id="element-927" data-mp="3e9pdrzml4nfl">
            <Stage n="1" title="The book" data-id="element-928" data-mp="3e8xqrd71zy0x">
              <p className="text-base leading-relaxed text-paper/70 md:text-lg" data-id="element-929" data-mp="3j80la3peqlun">
                Ideas, frameworks, stories, language, and offers
              </p>
            </Stage>
            <Down data-id="element-930" data-mp="3s8yq2o7o6xea" />
            <Stage n="2" title="A better reader path" data-id="element-931" data-mp="3s9gwgdo1qrzx">
              <Chips items={readerPath} data-id="element-932" data-mp="3s85pk4zb144l" />
            </Stage>
            <Down data-id="element-933" data-mp="1tudwt7ixlkvr" />
            <Stage n="3" title="Known context + intent" data-id="element-934" data-mp="1tux29oreagsw">
              <Chips items={knownContext} data-id="element-935" data-mp="1xue9ammgzm06" />
            </Stage>
            <Down data-id="element-936" data-mp="1xx0hmoh6q5jg" />
            <Stage n="4" title="Fitting business paths" data-id="element-937" data-mp="12kr75squc495">
              <div className="flex flex-wrap gap-2" data-id="element-938" data-mp="12j6e67k6ctde">
                {businessPaths.map(({
                label,
                Icon
              }) => <span key={label} className="inline-flex items-center gap-2 rounded-lg border border-seaglass/30 bg-white/[0.06] px-3 py-1.5 text-sm text-paper" data-id="element-939" data-mp="1cix6c2l9iwab">
                    <Icon className="h-4 w-4 flex-shrink-0 text-seaglass-light" aria-hidden="true" data-id="element-940" data-mp="17knbxu7d3zpr" />
                    {label}
                  </span>)}
              </div>
            </Stage>
            <Down data-id="element-941" data-mp="ewrkb3wngxzu" />
            <div className="rounded-3xl border-2 border-sand bg-sand/15 px-5 py-6 text-center md:px-6 md:py-7" data-id="element-942" data-mp="et3pm1fgy5jj">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sand/80" data-id="element-943" data-mp="tlvz0b48kk37">
                The destination
              </p>
              <p className="mt-2 font-serif text-3xl font-semibold text-sand md:text-5xl" data-id="element-944" data-mp="12qgvwur6mt5">
                Better-fit business opportunity
              </p>
            </div>

            <div className="mt-3 rounded-2xl border border-white/12 border-t-2 border-t-sand/40 bg-white/[0.03] p-4 md:p-5" data-id="element-945" data-mp="27saik0z4uqpb">
              <p className="text-base font-medium leading-relaxed text-paper/80 md:text-lg" data-id="element-946" data-mp="2cjsl2tgt0zce">
                The same build helps the author create and support those
                opportunities.
              </p>
              <div className="mt-3.5 grid gap-5 md:grid-cols-3 md:gap-6" data-id="element-947" data-mp="22th68bvl15qq">
                {enablementGroups.map(group => <div key={group.label} data-id="element-948" data-mp="2qj1tv82okr6c">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sand md:text-[0.8125rem]" data-id="element-949" data-mp="2vhd1dc5i6ple">
                      {group.label}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-950" data-mp="2hjyae2zlq9rb">
                      {group.items.map(item => <span key={item} className="rounded-lg border border-sand/25 bg-sand/10 px-3 py-1.5 text-[0.9375rem] leading-snug text-paper/85" data-id="element-951" data-mp="2mj1e9k3ui9xp">
                          {item}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-952" data-mp="37s4dnyzleyy2">
          <div className="mx-auto mt-6 max-w-3xl text-center md:mt-7" data-id="element-953" data-mp="37svzii3e1ib5">
            <p className="mx-auto max-w-3xl font-serif text-2xl leading-snug text-paper md:text-3xl" data-id="element-954" data-mp="330m9l229wi8v">
              <span className="block md:whitespace-nowrap" data-id="element-955" data-mp="2z02es5569qpw">
                Author Leverage helps the author get more business value
              </span>
              <span className="block md:whitespace-nowrap" data-id="element-956" data-mp="2tyd08fiqqux2">
                from the book by making it more useful to readers
              </span>
              <span className="block md:whitespace-nowrap" data-id="element-957" data-mp="2k1qjo2xg6q0u">
                and more usable across the business.
              </span>
            </p>
            <div className="mt-5 flex justify-center md:mt-6" data-id="element-958" data-mp="2g2hqp0a8ew6c">
              <Cta variant="primary" showArrow onClick={openDiagnostic} className="px-10 py-4 text-lg" data-id="element-959" data-mp="i9vmesbxuo25">
                Get Your Reader-to-Revenue Score
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}